/**
 * Shell colour + type tokens. Every value is a theme variable from
 * `appTheme.ts` / `globals.css` (`body.light-theme` flips them), with the
 * LIGHT value as the literal fallback for a surface that has not installed the
 * theme CSS. Layouts use these and nothing else, so light and dark come free.
 *
 * Text never goes below 13px (the shell's floor); greys are the AA-passing
 * `--color-text-secondary` / `--color-text-faint`.
 */
import type { CSSProperties } from 'react'

export const S = {
  /** Page / content ground. */
  bg: 'var(--color-bg, #eef1f5)',
  /** Bars, panels, cards (white in light). */
  panel: 'var(--color-bg-secondary, #ffffff)',
  /** Quiet fill: segmented controls, hover, a selected row. */
  surface2: 'var(--color-surface-2, #f0f3f7)',
  raised: 'var(--color-raised, #ffffff)',
  border: 'var(--color-border, #dde3ea)',
  borderStrong: 'var(--color-border-strong, #c7cfd9)',
  text: 'var(--color-text, #1c2430)',
  text2: 'var(--color-text-secondary, #465062)',
  faint: 'var(--color-text-faint, #687281)',
  accent: 'var(--color-accent, #0061D0)',
  accentHover: 'var(--color-accent-hvr, #0052b0)',
  accentWeak: 'var(--color-accent-weak, rgba(0, 97, 208, 0.10))',
  warning: 'var(--color-warning, #9a6700)',
  warningWeak: 'var(--color-warning-weak, rgba(154, 103, 0, 0.10))',
  error: 'var(--color-error, #cf222e)',
  shadow: 'var(--color-shadow, rgba(80, 90, 110, 0.18))',
  /** The "unsaved changes" dot — amber, distinct in lightness from text in both themes. */
  unsaved: '#E8A317',
  /** The pinned star. */
  star: '#E8A317',
  starStroke: '#B9800C',
  radius: 'var(--radius-md, 8px)',
  font: "var(--font-primary, 'IBM Plex Sans', 'Inter', system-ui, -apple-system, sans-serif)",
}

/** A plain icon button (≥ 36px; 44px where the layout has room). */
export function iconButton(size = 36): CSSProperties {
  return {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: size, height: size, flexShrink: 0, padding: 0,
    borderRadius: S.radius, border: 'none', background: 'transparent',
    color: S.text2, cursor: 'pointer', fontFamily: 'inherit',
  }
}

/** Hover / focus styling inline styles cannot express — mounted once per shell. */
export const SHELL_CSS = `
.tw-sh-btn{transition:background-color .12s,color .12s}
.tw-sh-btn:hover{background:var(--color-surface-2,#f0f3f7)!important;color:var(--color-text,#1c2430)!important}
.tw-sh-btn:focus-visible,.tw-sh-item:focus-visible{outline:2px solid var(--color-accent,#0061D0);outline-offset:1px}
.tw-sh-item:hover{background:var(--color-surface-2,#f0f3f7)}
.tw-sh-tip{display:none}
.tw-sh-info:hover .tw-sh-tip,.tw-sh-info:focus-within .tw-sh-tip{display:block}
.tw-sh-clip{white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
@media (prefers-reduced-motion:reduce){.tw-sh-btn{transition:none}}
`
